import { useRef, useState } from 'react';

export type UseAmountFieldDraftOptions = {
  initialDraft: number | '';
  committedAmount: number | null | undefined;
  onCommit: (amount: number) => void;
  /**
   * While true, an emptied blur reverts the draft back to blank instead of
   * the last committed amount — used by the Bill Setup Amount card for a
   * manually-added series, whose committed amount starts as a placeholder
   * zero rather than a real user-entered value. Cleared permanently after
   * the first real commit.
   */
  revertToBlank?: boolean;
};

/**
 * Local draft state for a Series amount field, not bound straight to the
 * form's setters — an emptied amount should never reach the shared form
 * (nothing to show an error state for, nothing for autosave to persist) and
 * should just snap back to the last committed amount on blur. Shared between
 * the Bill Setup Amount card and the Create/Edit Series Amount card.
 */
export function useAmountFieldDraft({
  initialDraft,
  committedAmount,
  onCommit,
  revertToBlank = false,
}: UseAmountFieldDraftOptions): {
  amountDraft: number | '';
  setAmountDraft: (value: number | '') => void;
  commitAmount: () => void;
} {
  const [amountDraft, setAmountDraft] = useState<number | ''>(initialDraft);
  const revertToBlankRef = useRef(revertToBlank);
  const lastCommittedAmountRef = useRef(committedAmount);

  // `committedAmount` can change from outside this field — e.g. switching a
  // series between Income/Expense flips its sign. Adopt that change unless
  // the draft has already diverged from the previous committed value (an
  // active, uncommitted edit in progress), which should win instead.
  if (committedAmount !== lastCommittedAmountRef.current) {
    if (amountDraft === lastCommittedAmountRef.current) {
      setAmountDraft(committedAmount ?? '');
    }
    lastCommittedAmountRef.current = committedAmount;
  }

  const commitAmount = () => {
    if (amountDraft === '') {
      setAmountDraft(revertToBlankRef.current ? '' : (committedAmount ?? ''));
      return;
    }
    if (amountDraft !== committedAmount) {
      revertToBlankRef.current = false;
      onCommit(amountDraft);
    }
  };

  return { amountDraft, setAmountDraft, commitAmount };
}
